<script
    context="module"
    lang="ts"
>
    import {
        getCurrentVariant,
        getProductVariantsThatDifferInOptions,
        getProductVariantsThatDifferInOptionsGivenSelectedOption,
        mapVariantImages,
    } from "../../../functions/helper/product"

    const optionTypes: { name: OptionNames; inputName: string }[] = [
        { name: "Farbe", inputName: "color" },
        { name: "Größe", inputName: "size" },
    ]

    function updateSelectedOptions(
        selectedOptions: BKDFProductSelectedOption[],
        optionName: OptionNames,
        value: string
    ): BKDFProductSelectedOption[] {
        const newOptions = [...selectedOptions]
        const index = newOptions.findIndex((option) => option.name === optionName)
        if (index !== -1) newOptions[index].value = value
        else newOptions.push({ name: optionName, value })

        return newOptions
    }

    function updateColorVariants(
        product: BKDFProduct,
        selectedOptions: BKDFProductSelectedOption[]
    ): { possibleColorVariants: BKDFProductVariant[]; colorVariantImageMappedObject: { [key: string]: Image[] } } {
        const selectedSizeOption = selectedOptions.find((option) => option.name === "Größe")
        const possibleColorVariants = getProductVariantsThatDifferInOptionsGivenSelectedOption(
            selectedSizeOption,
            product.variants
        )
        const colorVariantImageMappedObject = mapVariantImages(product, possibleColorVariants)
        return { possibleColorVariants, colorVariantImageMappedObject }
    }

    function updateSizeVariants(
        product: BKDFProduct,
        selectedOptions: BKDFProductSelectedOption[]
    ): BKDFProductVariant[] {
        const selectedColorOption = selectedOptions.find((option) => option.name === "Farbe")
        return getProductVariantsThatDifferInOptionsGivenSelectedOption(selectedColorOption, product.variants)
    }
</script>

<script lang="ts">
    import { createEventDispatcher, onMount } from "svelte"
    import ActionRow from "./ActionRow.svelte"
    import ProductColorPreviewList from "./ProductColorPreviewList.svelte"
    import ProductSizesPreviewList from "./ProductSizesPreviewList.svelte"

    export let product: BKDFProduct

    const dispatch = createEventDispatcher()

    let possibleColorVariants: BKDFProductVariant[] = [],
        colorVariantImageMappedObject: { [key: string]: Image[] } = {},
        possibleSizeVariants: BKDFProductVariant[] = [],
        selectedVariant: BKDFProductVariant,
        selectedOptions: BKDFProductSelectedOption[] = []

    function handleOptionChange(optionName: OptionNames, value: string) {
        selectedOptions = updateSelectedOptions(selectedOptions, optionName, value)
        const currentVariant = getCurrentVariant(product, selectedOptions)
        if (!!currentVariant) {
            ;({ possibleColorVariants, colorVariantImageMappedObject } = updateColorVariants(
                product,
                selectedOptions.filter((option) => option?.name === "Größe")
            ))
            handleVariantChange(currentVariant)
        }
    }

    function handleVariantChange(variant: BKDFProductVariant) {
        selectedVariant = variant
        dispatch("variantChange", {
            variant: selectedVariant,
            previewImages: colorVariantImageMappedObject[selectedVariant.id],
        })
    }

    const reactOnSizingChange = () => {
        ;({ possibleColorVariants, colorVariantImageMappedObject } = updateColorVariants(
            product,
            selectedOptions.filter((option) => option?.name === "Größe")
        ))
    }

    function reactOnColorChange() {
        possibleSizeVariants = updateSizeVariants(
            product,
            selectedOptions.filter((option) => option?.name === "Farbe")
        )
        if (!selectedVariant)
            dispatch("colorChange", {
                previewImages:
                    colorVariantImageMappedObject[Object.keys(mapVariantImages(product, possibleSizeVariants))[0]],
            })
    }

    onMount(() => {
        possibleColorVariants = getProductVariantsThatDifferInOptions(["Farbe"], product.variants)
        possibleSizeVariants = getProductVariantsThatDifferInOptions(["Größe"], product.variants)
        colorVariantImageMappedObject = mapVariantImages(product, possibleColorVariants)
        if (!selectedVariant)
            dispatch("colorChange", {
                previewImages: colorVariantImageMappedObject[Object.keys(colorVariantImageMappedObject)[0]],
            })
    })
</script>

<section>
    <section id="configurator">
        {#each optionTypes as { name }}
            {#if name == "Farbe"}
                <ProductColorPreviewList
                    variants="{possibleColorVariants}"
                    selectedSize="{selectedOptions.find((o) => o.name === 'Größe')}"
                    selectedColor="{selectedOptions.find((o) => o.name === 'Farbe')}"
                    product="{product}"
                    backgroundColor="white"
                    on:colorChange="{(event) => {
                        handleOptionChange(name, event.detail.color)
                        reactOnColorChange()
                    }}"
                />
            {:else if name == "Größe"}
                <ProductSizesPreviewList
                    possibleSizeVariants="{possibleSizeVariants}"
                    selectedSize="{selectedOptions.find((o) => o.name === 'Größe')}"
                    product="{product}"
                    selectedColor="{selectedOptions.find((o) => o.name === 'Farbe')}"
                    on:sizeChange="{(e) => {
                        handleOptionChange(name, e.detail.size)
                        reactOnSizingChange()
                    }}"
                />
            {/if}
        {/each}
    </section>

    <ActionRow variant="{selectedVariant}" />
</section>

<style lang="less">
    #configurator {
        display: flex;
        flex-direction: column;
        gap: 24px;
        margin-top: 24px;
        color: var(--text-invers-100);
    }
</style>
